import { Globals } from './Globals';
import { AdvancedActions } from '../game/AdvancedActions';
import { ENGINE_LOGGING_ENABLED } from '../utils/Constants';

const BUILTIN_SCRIPT_EVENTS = new Set([
	'<<on game start>>',
	'<<on game load>>',
	'<<before game save>>',
	'<<on game version mismatch>>',
]);

type ScriptEntry = { name: string; script: string };

function logScriptRunner(message: string, payload?: any): void {
	if (!ENGINE_LOGGING_ENABLED) return;
	// eslint-disable-next-line no-console
	console.log(`[ScriptRunner] ${message}`, payload || '');
}

function normalizeEventName(name: string): string {
	return String(name || '').trim().toLowerCase();
}

function parseScriptEntry(entry: any): ScriptEntry | null {
	if (!entry) return null;
	if (Array.isArray(entry)) {
		const name = String(entry[0] || '').trim();
		const script = String(entry[1] || '').trim();
		if (!name || !script) return null;
		return { name, script };
	}
	if (typeof entry === 'object') {
		const name = String(entry.Name || entry.name || '').trim();
		const script = String(entry.Script || entry.script || '').trim();
		if (!name || !script) return null;
		return { name, script };
	}
	return null;
}

function getScriptEntries(game: any): ScriptEntry[] {
	const data = game?.ScriptData;
	if (!Array.isArray(data)) return [];
	return data.map(parseScriptEntry).filter((entry): entry is ScriptEntry => Boolean(entry));
}

function isEventScript(name: string): boolean {
	return BUILTIN_SCRIPT_EVENTS.has(normalizeEventName(name));
}

function getGlobalScripts(game: any): ScriptEntry[] {
	return getScriptEntries(game).filter((entry) => !isEventScript(entry.name));
}

function getEventScripts(game: any, eventName: string): ScriptEntry[] {
	const match = normalizeEventName(eventName);
	return getScriptEntries(game).filter((entry) => normalizeEventName(entry.name) === match);
}

async function runScriptsSequentially(scripts: ScriptEntry[]): Promise<void> {
	for (const entry of scripts) {
		logScriptRunner('running script', entry.name);
		await AdvancedActions.executeAdvancedScriptText(entry.script, { name: entry.name });
	}
}

export async function runGlobalScriptsOnce(): Promise<void> {
	if (Globals.globalScriptsRan) return;
	Globals.globalScriptsRan = true;
	const scripts = getGlobalScripts((window as any).TheGame);
	if (scripts.length === 0) return;
	const scriptTexts = scripts.map((entry) => entry.script);
	await AdvancedActions.executeAdvancedGlobalScripts(scriptTexts);
}

export async function runEventScripts(eventName: string): Promise<void> {
	const scripts = getEventScripts((window as any).TheGame, eventName);
	if (scripts.length === 0) return;
	await runScriptsSequentially(scripts);
}
